//#region IMPORTS
import {
  fetchShippableOrdersAsync,
  fetchApprovableOrdersAsync,
} from "../orders/ordersSlice";
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { useEffect, useRef, useState } from "react";
import { fetchBasketReasonsAsync } from "../basketReason/basketReasonSlice";
import { Redirect, Route, Switch } from "react-router-dom";
import { fetchPromosAsync } from "../promos/promosSlice";
import { useTranslation } from "react-i18next";
import { closeDash } from "./dashboardSlice";
import { toast } from "react-toastify";
import InstoreTemplateDetailPage from "../instoreTemplates/InstoreTemplateDetailPage";
import ProductVersionsDetailPage from "../productVersions/ProductVersionsDetailPage";
import InstoreOrderDetailPage from "../instoreOrders/InstoreOrderDetailPage";
import TranslationManagerPage from "../translationManager/TranslationManagerPage";
import InstoreOrderTypesPage from "../instoreOrderTypes/InstoreOrderTypesPage";
import InstoreTemplatesPage from "../instoreTemplates/InstoreTemplatesPage";
import ProductVersionsPage from "../productVersions/ProductVersionsPage";
import ProductsDetailPage from "../products/ProductsDetailPage";
import DashboardContainer from "../../components/dashboard/DashboardContainer";
import OrdersHandlingPage from "../orders/OrdersHandlingPage";
import InstoreOrdersPage from "../instoreOrders/InstoreOrdersPage";
import OrdersHistoryPage from "../orders/OrdersHistoryPage";
import OrdersApprovePage from "../orders/OrdersApprovePage";
import InstoreThemesPage from "../instoreThemes/InstoreThemesPage";
import OrdersShippedPage from "../orders/OrdersShippedPage";
import OrdersPendingPage from "../orders/OrdersPendingPage";
import DashboardMessage from "../../components/dashboard/DashboardMessage";
import InstoreTypesPage from "../instoreTypes/InstoreTypesPage";
import usePickerManager from "../../hooks/usePickerManager";
import OrdersManagePage from "../orders/OrdersManagePage";
import CheckoutConfirm from "../checkout/CheckoutConfirm";
import useAdminManager from "../../hooks/useAdminManager";
import OrderDetailPage from "../orders/OrderDetailPage";
import BackOrderPage from "../backorder/BackOrderPage";
import ItemBigImage from "../../components/itemsGrid/ItemBigImage";
import CheckoutPage from "../checkout/CheckoutPage";
import SettingsPage from "../settings/SettingsPage";
import ProductsPage from "../products/ProductsPage";
import useHQmanager from "../../hooks/useHQmanager";
import StoreDetails from "../stores/StoreDetails";
import PickerRoute from "../../components/routes/PickerRoute";
import FeaturePage from "../faq/FeaturePage";
import StoresPage from "../stores/StoresPage";
import BasketPage from "../basket/BasketPage";
import PromosPage from "../promos/PromosPage";
import AdminRoute from "../../components/routes/AdminRoute";
import StoreRoute from "../../components/routes/StoreRoute";
import UsersPage from "../users/UsersPage";
import HqRoute from "../../components/routes/HqRoute";
import Home from "../home/Home";
//#endregion

export default function DashboardV2() {
  //#region SETUP
  // Ref
  const toastId: any = useRef();

  // Translation
  const { t } = useTranslation();

  // Redux
  const dispatch = useAppDispatch();
  const { promosLoaded } = useAppSelector((state) => state.promos);

  // Local state
  const [messageShown, setMessageShown] = useState<boolean>(false);

  // Roles
  const { user } = useAppSelector((state) => state.account);
  const { refreshingTransmart } = useAppSelector((state) => state.promos);
  const isPicker = usePickerManager(user);
  const isAdmin = useAdminManager(user);
  const isHq = useHQmanager(user);

  // Check device
  const windowSize = useRef([window.innerWidth, window.innerHeight]);

  //#endregion

  //#region LOGIC
  // Close Dashboard on smaller screens
  useEffect(() => {
    if (windowSize.current[0] < 996) {
      dispatch(closeDash());
    }
  }, [dispatch]);

  // Load promos
  useEffect(() => {
    if (!promosLoaded) {
      dispatch(fetchPromosAsync());
    }
  }, [promosLoaded, dispatch]);

  // Load basket reasons
  useEffect(() => {
    dispatch(fetchBasketReasonsAsync());
  }, [dispatch]);

  // Load role specific orders
  useEffect(() => {
    const fetchRoleSpecificOrders = async () => {
      (isAdmin || isHq) && (await dispatch(fetchApprovableOrdersAsync()));
      (isAdmin || isPicker) && (await dispatch(fetchShippableOrdersAsync()));
    };
    fetchRoleSpecificOrders();
  }, [dispatch, isAdmin, isHq, isPicker]);

  // ShowUpdating badge
  useEffect(() => {
    if (refreshingTransmart) {
      toastId.current = toast("Updating transmart connection", {
        autoClose: false,
        theme: "dark",
        type: "info",
        closeOnClick: false,
      });
    } else {
      toast.update(toastId.current, {
        render: "Update succesfull",
        type: "success",
        autoClose: 5000,
      });
    }
  }, [refreshingTransmart]);
  //#endregion

  //#region RENDER
  return (
    <DashboardContainer>
      <Switch>
        {/* SETTINGS */}
        <AdminRoute exact path="/settings" component={SettingsPage} />

        {/* USERS */}
        <AdminRoute exact path="/users" component={UsersPage} />

        {/* TRANSLATIONMANAGER */}
        <AdminRoute
          exact
          path="/translations"
          component={TranslationManagerPage}
        />

        {/* PRODUCTVERSIONS */}
        <AdminRoute
          path="/productversions/:id"
          component={ProductVersionsDetailPage}
        />
        <AdminRoute
          exact
          path="/productversions"
          component={ProductVersionsPage}
        />

        {/* PRODUCTS */}
        <AdminRoute path="/products/:id" component={ProductsDetailPage} />
        <AdminRoute exact path="/products" component={ProductsPage} />

        {/* STORES */}
        <StoreRoute exact path="/stores/manage" component={StoreDetails} />
        <StoreRoute path="/stores/manage/:id" component={StoreDetails} />
        <StoreRoute exact path="/stores" component={StoresPage} />

        {/* PROMOS */}
        <Route exact path="/promos" component={PromosPage} />

        {/* ORDERS TO APPROVE */}
        <HqRoute path="/approve/order/:id">
          <OrderDetailPage />
        </HqRoute>
        <HqRoute path="/approve/:code">
          <OrdersApprovePage title={t("rituals.approvableBackOrders")} />
        </HqRoute>
        <HqRoute path="/approve">
          <OrdersApprovePage title={t("rituals.approvableBackOrders")} />
        </HqRoute>

        {/* INSTORE ORDERS */}
        <HqRoute path="/instoreorders/create">
          <InstoreOrderDetailPage create />
        </HqRoute>
        <AdminRoute
          path="/instoreorders/edit/:id"
          component={InstoreOrderDetailPage}
        />
        <HqRoute path="/instoreorders/:id">
          <InstoreOrderDetailPage readonly/>
        </HqRoute>
        <HqRoute path="/instoreorders">
          <InstoreOrdersPage title={t("rituals.instoreOrders")} />
        </HqRoute>

        {/* INSTORE TEMPLATES */}
        <HqRoute path="/instoretemplates/:id">
          <InstoreTemplateDetailPage />
        </HqRoute>
        <HqRoute path="/instoretemplates">
          <InstoreTemplatesPage />
        </HqRoute>

        {/* INSTORE THEMES */}
        <HqRoute path="/instorethemes">
          <InstoreThemesPage />
        </HqRoute>

        {/* INSTORE TYPES */}
        <HqRoute path="/instoretypes">
          <InstoreTypesPage />
        </HqRoute>

        {/* INSTORE ORDER TYPES */}
        <HqRoute path="/instoreordertypes">
          <InstoreOrderTypesPage />
        </HqRoute>

        {/* CHECKOUT */}
        <Route path="/checkout/:id" component={CheckoutConfirm} />
        <Route path="/checkout" component={CheckoutPage} />

        {/* BASKET */}
        <Route path="/basket" component={BasketPage} />

        <AdminRoute exact path="/handling" component={OrdersHandlingPage} />

        {/* ORDERS WITH SUBORDERS SHIPPED */}
        <Route path="/shipped" exact>
          <OrdersShippedPage title={t("rituals.shippedBackOrders")} />
        </Route>

        {/* ORDERS WITH SUBORDERS PENDING */}
        <Route path="/pending" exact>
          <OrdersPendingPage title={t("rituals.pendingBackOrders")} />
        </Route>

        {/* ORDERS WITH SUBORDERS ORDERED */}
        <PickerRoute path="/manage" exact>
          <OrdersManagePage title={t("rituals.orderedBackOrders")} />
        </PickerRoute>

        {/* ALL ORDERS WITH SUBORDERS */}
        <Route path="/orders/:id" component={OrderDetailPage} />
        <Route path="/orders" exact>
          <OrdersHistoryPage
            title={
              !isAdmin && !isPicker && !isHq
                ? t("rituals.orderHistory")
                : t("rituals.allOrders")
            }
          />
        </Route>

        {/* FEATURE PAGE */}
        <Route path="/featurelist" exact component={FeaturePage} />

        {/* BACKORDER */}
        <Route path="/backorder/promo/:id" component={BackOrderPage} />

        {/* BIGIMAGE */}
        <Route path="/image/:id" component={ItemBigImage} />

        {/* DEFAULT */}
        <Route path="/" exact component={Home} />
        <Redirect to="/not-found" />
      </Switch>
      <DashboardMessage
        closeHandler={() => setMessageShown(true)}
        hide={messageShown || isAdmin}
      />
    </DashboardContainer>
  );
  //#endregion
}
